import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Checkbox, FormControlLabel, Button, CircularProgress, Alert, Chip, Stack,
  Dialog, DialogContent, DialogActions, TextField, MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ScienceIcon from "@mui/icons-material/Science";
import UserContext from "./UserContext";
import ThemeToggle from "./ThemeToggle";
import {
  TelehealthDetailsFields, EMPTY_ADDRESS, EMPTY_CHILD, PHENOTYPE_QUESTIONS, PHQ_QUESTIONS,
} from "./telehealthShared";
import { PayItForwardDialog, PayItForwardExtra } from "./PayItForward";
import { isValidName, isValidPhone, sanitizePhoneInput } from "./validators";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const BRAND = { navy: "#0a0e27", navyDark: "#0f1233", accent: "#637cdb" };

const STATUS_COLOR = {
  completed: "success",
  sent: "warning",
  pending: "warning",
  covered: "success",
};

const STATUS_LABEL = {
  completed: "Paid",
  sent: "Payment link sent",
  pending: "Pending",
  // Bundle entitlement (2026-08-17) — no direct payment_requests doc for
  // this exact item_key, but covered by all_panels (or vice versa).
  covered: "Included in your bundle",
};

function formatCents(cents) {
  return `$${(cents / 100).toFixed(2).replace(/\.00$/, "")}`;
}

// Repeatable items (charged per-use, not once-only) must stay requestable
// even after a prior request completed — only an already-outstanding
// unpaid link should block a new checkbox, matching the backend's
// _REPEATABLE_PAYMENT_ITEMS exception in POST /me/payment-requests.
// upload_interpretation (the only repeatable item) was removed 2026-08-17 —
// backend _REPEATABLE_PAYMENT_ITEMS is now empty too, kept in sync here.
const REPEATABLE_ITEMS = new Set();

function ItemRow({ itemKey, item, existing, checked, onToggle, isDark }) {
  const status = existing?.status;
  const repeatable = REPEATABLE_ITEMS.has(itemKey);
  const blocksNewRequest = Boolean(existing) && !(repeatable && status === "completed");

  return (
    <Box
      onClick={() => !blocksNewRequest && onToggle(itemKey)}
      sx={{
        display: "flex", alignItems: "flex-start", gap: 1.5,
        borderRadius: "10px", px: 2, py: 1.5,
        cursor: blocksNewRequest ? "default" : "pointer",
        border: `2px solid ${checked ? BRAND.navy : (isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.1)")}`,
        bgcolor: checked
          ? (isDark ? "rgba(99,124,219,0.15)" : "rgba(99,124,219,0.06)")
          : "transparent",
        opacity: blocksNewRequest ? 0.75 : 1,
        transition: "all 0.15s",
      }}
    >
      {!blocksNewRequest && (
        <Checkbox
          checked={checked}
          onChange={() => onToggle(itemKey)}
          onClick={(e) => e.stopPropagation()}
          sx={{ p: 0, mt: 0.2, color: isDark ? "rgba(255,255,255,0.3)" : "rgba(10,14,39,0.25)", "&.Mui-checked": { color: BRAND.navy } }}
        />
      )}
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b" }}>
            {formatCents(item.amount_cents)} — {item.label}
          </Typography>
          {existing && (
            <Chip size="small" label={STATUS_LABEL[status] || status} color={STATUS_COLOR[status] || "default"} />
          )}
        </Box>
        <Typography sx={{ fontSize: 12.5, color: isDark ? "#64748b" : "#94a3b8", mt: 0.25 }}>
          {item.description}
        </Typography>
        {repeatable && status === "completed" && (
          <Typography sx={{ fontSize: 11.5, color: BRAND.accent, fontWeight: 600, mt: 0.5 }}>
            Charged per upload — select again anytime you have another report to interpret.
          </Typography>
        )}
        {existing?.receipt_url && (
          <Box
            component="a" href={existing.receipt_url} target="_blank" rel="noopener noreferrer"
            sx={{ fontSize: 12, color: BRAND.accent, fontWeight: 600, mt: 0.5, display: "inline-block" }}
          >
            View receipt →
          </Box>
        )}
      </Box>
    </Box>
  );
}

// 2026-09-27 addition — "pick a previous kid instead of retyping" dropdown,
// backed by GET /telehealth/my-kids. Selecting a kid pre-fills first_name/
// last_name/date_of_birth via `onPick`; picking "Someone new" (the default)
// just leaves whatever's already in the free-text fields below it alone —
// this is a convenience on top of those fields, not a replacement for them
// (gender/race for telehealth's child form still need to be picked
// separately, since parent_kids doesn't store those).
function KidPicker({ kids, onPick, isDark, inputSx }) {
  if (!kids || kids.length === 0) return null;
  return (
    <TextField
      select size="small" fullWidth sx={{ ...inputSx, mb: 1.5 }}
      label="Pick a previous kid (optional)"
      defaultValue=""
      onChange={(e) => {
        const idx = e.target.value;
        if (idx === "") return;
        onPick(kids[Number(idx)]);
      }}
    >
      <MenuItem value="">Someone new — I'll enter details below</MenuItem>
      {kids.map((k, i) => (
        <MenuItem key={i} value={String(i)}>
          {k.first_name} {k.last_name}{k.date_of_birth ? ` (DOB ${k.date_of_birth})` : ""}
        </MenuItem>
      ))}
    </TextField>
  );
}

export default function MyPayments() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const token = user?.token;

  const [catalog, setCatalog] = useState(null);
  const [existingRequests, setExistingRequests] = useState([]);
  const [selected, setSelected] = useState([]);
  const [subscriptionOptin, setSubscriptionOptin] = useState(false);
  const [coveredItemKeys, setCoveredItemKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  // ── Order-more: telehealth + other-lab-upload (2026-08-18) ──────────────
  // These previously only existed inside the registration wizard, with no
  // way for an already-registered patient to order either one again later.
  // /telehealth/orders and /other-lab/orders already reuse the logged-in
  // session (see _get_or_create_parent's Authorization check) — this just
  // adds the missing entry points.
  const [pendingTelehealth, setPendingTelehealth] = useState([]);
  const [pendingOtherLab, setPendingOtherLab] = useState([]);

  const [telehealthOpen, setTelehealthOpen] = useState(false);
  const [telehealthSubmitting, setTelehealthSubmitting] = useState(false);
  const [telehealthError, setTelehealthError] = useState(null);
  // Pre-filled from the logged-in user's own account (added 2026-09-03) —
  // this order already runs through the logged-in session (see comment
  // above), so re-typing your own phone/DOB every time was pure friction;
  // the backend already has both on file. Still editable in case either
  // is stale or wasn't captured at the account's own registration.
  const [parentPhone, setParentPhone] = useState(user?.phone || "");
  const [parentDob, setParentDob] = useState(user?.date_of_birth || "");
  const [child, setChild] = useState({ ...EMPTY_CHILD });
  const [shippingAddress, setShippingAddress] = useState({ ...EMPTY_ADDRESS });
  const [billingAddress, setBillingAddress] = useState({ ...EMPTY_ADDRESS });
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [phenotypeAnswers, setPhenotypeAnswers] = useState({});
  const [phqAnswers, setPhqAnswers] = useState({});
  const [medications, setMedications] = useState([]);
  const [payItForwardQty, setPayItForwardQty] = useState(0);

  const [otherLabOpen, setOtherLabOpen] = useState(false);
  const [otherLabSubmitting, setOtherLabSubmitting] = useState(false);
  const [otherLabError, setOtherLabError] = useState(null);
  const [otherLabFile, setOtherLabFile] = useState(null);
  // 2026-09-27 addition — this order can now be for the account holder
  // directly (unchanged default) OR their child. Minimal fields only
  // (name + DOB) — unlike the telehealth dialog's full `child` state above,
  // this flow needs nothing beyond enough to build a distinct patient
  // identity; no address/questionnaire/medications here.
  const [otherLabIsForChild, setOtherLabIsForChild] = useState(false);
  const [otherLabChild, setOtherLabChild] = useState({ first_name: "", last_name: "", date_of_birth: "" });

  const [pifDialogOpen, setPifDialogOpen] = useState(false);

  const bg = isDark ? "#0a0f1e" : "#f0f4ff";
  const cardBg = isDark ? "#111827" : "#ffffff";

  const handleOpenBillingPortal = async () => {
    setPortalLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/billing/portal-session`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = res.data.url;
    } catch (err) {
      setPortalLoading(false);
      setError(err.response?.data?.detail || "Couldn't open the billing portal. Please try again.");
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/me/payment-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExistingRequests(res.data.payment_requests || []);
      setCatalog(res.data.catalog || {});
      setCoveredItemKeys(res.data.covered_item_keys || []);
      setPendingTelehealth(res.data.pending_telehealth_orders || []);
      setPendingOtherLab(res.data.pending_other_lab_orders || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load your payment requests.");
    } finally {
      setLoading(false);
    }
  };

  // 2026-09-27 addition — backs the "pick a previous kid" dropdown in both
  // the telehealth and Other-Labs "order more" dialogs, so a repeat order
  // for the same kid doesn't require retyping their name/DOB (and risking
  // the fragmentation the picker exists to prevent — see
  // _get_or_register_kid, backend main.py). Best-effort: an empty/failed
  // fetch just means the picker has nothing to offer, not a page error.
  const [myKids, setMyKids] = useState([]);
  const loadMyKids = async () => {
    try {
      const res = await axios.get(`${API_BASE}/telehealth/my-kids`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyKids(res.data.kids || []);
    } catch {
      setMyKids([]);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/", { replace: true });
      return;
    }
    load();
    loadMyKids();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Stripe redirects an already-logged-in patient back here after a panel
  // checkout (success_url=/my-payments?panels=success). Previously this
  // query param was ignored and the page just reloaded silently — now it
  // shows a confirmation. No temp-password step here: this patient already
  // has their own login, unlike the registration-time flow.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const p = params.get("panels");
    if (p === "success") {
      setSuccessMsg("Payment successful — your purchase is confirmed and your report access is being updated. A receipt has been emailed to you.");
    } else if (p === "cancelled") {
      setError("Checkout was cancelled — no payment was taken. You can try again whenever you're ready.");
    }
    if (p) window.history.replaceState({}, "", "/my-payments");
  }, []);

  const existingByKey = useMemo(() => {
    const map = {};
    for (const pr of existingRequests) map[pr.item_key] = pr;
    // Bundle entitlements (2026-08-17): synthesize an entry for any
    // item_key covered by a completed bundle but with no direct doc of its
    // own — e.g. owning all_panels covers the 3 components too, and vice
    // versa. Real docs take priority; this only fills in gaps.
    for (const key of coveredItemKeys) {
      if (!map[key]) map[key] = { item_key: key, status: "covered" };
    }
    return map;
  }, [existingRequests, coveredItemKeys]);

  // Only the 4 optional cash-pay panel items use the checkbox-list ItemRow
  // pattern — the $25 co-pay is always created at registration (mandatory),
  // so it only ever shows its existing status, never a new checkbox.
  // monthly_ai_subscription (2026-08-17) is handled entirely separately via
  // the subscriptionOptin checkbox below, not through `selected` — the
  // backend's item_keys validation never accepted it as a panel item_key.
  // upload_interpretation was removed 2026-08-17 (see backend PAYMENT_ITEMS).
  const panelKeys = ["drug_metabolism", "opioid_risk", "pain_perception", "all_panels"];

  const toggle = (key) => {
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  // CHANGED 2026-08-17: this now builds ONE combined Stripe Checkout
  // Session covering every selected panel (+ the $0.99/mo add-on if opted
  // in and not already subscribed) and redirects there directly — no more
  // "check your email for N separate Payment Links." Backend response
  // shape changed from {payment_requests: [...]} to {checkout_url: ...}.
  const handleSubmit = async () => {
    if (selected.length === 0 && !subscriptionOptin) return; // nothing selected at all
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await axios.post(
        `${API_BASE}/me/payment-requests`,
        { item_keys: selected, subscription_optin: subscriptionOptin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setSuccessMsg(res.data.message || "Nothing new to request.");
      setSelected([]);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit your request.");
    } finally {
      setSubmitting(false);
    }
  };

  const [nameFirst, nameLast] = (user?.name || "").split(/\s+/, 2);

  const telehealthInputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(10,14,39,0.018)",
      fontSize: 15,
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.14)" },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(10,14,39,0.3)" },
      "&.Mui-focused fieldset": { borderColor: BRAND.navy, borderWidth: "2px" },
    },
    "& .MuiOutlinedInput-input": { py: "12px", px: "14px" },
  };

  const canSubmitTelehealth = () => {
    if (!isValidPhone(parentPhone) || !parentDob) return false;
    if (!isValidName(child.first_name) || !isValidName(child.last_name) || !child.date_of_birth || !child.gender || !child.race) return false;
    if (!shippingAddress.line1.trim() || !shippingAddress.city.trim() || !shippingAddress.state.trim() || !shippingAddress.zip.trim()) return false;
    if (!sameAsShipping && (!billingAddress.line1.trim() || !billingAddress.city.trim() || !billingAddress.state.trim() || !billingAddress.zip.trim())) return false;
    if (!PHENOTYPE_QUESTIONS.every((q) => phenotypeAnswers[q])) return false;
    if (!PHQ_QUESTIONS.every((q) => phqAnswers[q])) return false;
    return true;
  };

  const handleTelehealthSubmit = async () => {
    setTelehealthSubmitting(true);
    setTelehealthError(null);
    try {
      const payload = {
        parent: {
          first_name: nameFirst || "", last_name: nameLast || "",
          email: user.email, phone: parentPhone.trim(), date_of_birth: parentDob,
        },
        child,
        shipping_address: shippingAddress,
        billing_address: sameAsShipping ? shippingAddress : billingAddress,
        questionnaire: {
          phenotype_answers: phenotypeAnswers,
          phq_answers: phqAnswers,
          medications: medications.filter((m) => m.medication.trim()),
        },
        subscription_optin: false,
        pay_it_forward_quantity: payItForwardQty,
      };
      const res = await axios.post(`${API_BASE}/telehealth/orders`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setTelehealthError(err.response?.data?.detail || "Failed to start checkout. Please try again.");
      setTelehealthSubmitting(false);
    }
  };

  const otherLabChildValid =
    !otherLabIsForChild ||
    (isValidName(otherLabChild.first_name) && isValidName(otherLabChild.last_name) && !!otherLabChild.date_of_birth);

  const handleOtherLabSubmit = async () => {
    if (!otherLabFile || !isValidPhone(parentPhone) || !parentDob || !otherLabChildValid) return;
    setOtherLabSubmitting(true);
    setOtherLabError(null);
    try {
      const form = new FormData();
      form.append("file", otherLabFile);
      form.append("first_name", nameFirst || "");
      form.append("last_name", nameLast || "");
      form.append("email", user.email);
      form.append("phone", parentPhone.trim());
      form.append("date_of_birth", parentDob);
      form.append("is_for_child", otherLabIsForChild ? "true" : "false");
      if (otherLabIsForChild) {
        form.append("child_first_name", otherLabChild.first_name.trim());
        form.append("child_last_name", otherLabChild.last_name.trim());
        form.append("child_date_of_birth", otherLabChild.date_of_birth);
      }
      const res = await axios.post(`${API_BASE}/other-lab/orders`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      // detail can be a plain string OR (new: account_exists_please_login,
      // structurally unreachable here since this whole page is already
      // logged-in — see _get_or_create_parent's Bearer-token branch — but
      // handled defensively anyway rather than risking "[object Object]").
      const detail = err.response?.data?.detail;
      setOtherLabError((typeof detail === "string" ? detail : detail?.message) || "Failed to start checkout. Please try again.");
      setOtherLabSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: BRAND.navy }} />
      </Box>
    );
  }

  const copayRequest = existingByKey["copay"];
  const copayItem = catalog?.copay;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg }}>
      <Box sx={{ bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(10,14,39,0.97)", borderBottom: "2px solid rgba(99,124,219,0.3)" }}>
        <Box sx={{ maxWidth: 900, mx: "auto", px: 3, height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box component={Link} to="/home" sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
            <img src="/logo-light.png" alt="Proove Genomics" style={{ height: 20, width: "auto" }} />
          </Box>
          <ThemeToggle />
        </Box>
      </Box>

      <Box sx={{ maxWidth: 900, mx: "auto", px: 3, py: 5 }}>
        <Button
          component={Link} to="/home"
          startIcon={<ArrowBackIcon sx={{ fontSize: "16px !important" }} />}
          sx={{
            mb: 2, px: 0, minWidth: 0, textTransform: "none", fontSize: 13, fontWeight: 600,
            color: isDark ? "#94a3b8" : "#64748b",
            "&:hover": { bgcolor: "transparent", color: BRAND.accent },
          }}
        >
          Back to Home
        </Button>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.15em", color: BRAND.accent, mb: 1 }}>
          BILLING
        </Typography>
        <Typography sx={{ fontSize: 28, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 0.5 }}>
          My Payments
        </Typography>
        <Typography sx={{ fontSize: 14, color: isDark ? "#94a3b8" : "#64748b", mb: 4 }}>
          Review your co-pay status and request additional Proove testing at any time.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
        {successMsg && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}

        {(pendingTelehealth.length > 0 || pendingOtherLab.length > 0) && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            {pendingTelehealth.map((o) => (
              <Box key={o.order_id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, "&:not(:last-child)": { mb: 1 } }}>
                <span>Telehealth order for {o.child_first_name} {o.child_last_name} is waiting on payment.</span>
                <Button size="small" onClick={() => setTelehealthOpen(true)} sx={{ textTransform: "none", flexShrink: 0 }}>Complete payment</Button>
              </Box>
            ))}
            {pendingOtherLab.map((o) => (
              <Box key={o.order_id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, "&:not(:last-child)": { mb: 1 } }}>
                <span>
                  Other-lab upload ({o.original_filename}){o.is_for_child ? ` for ${o.child_first_name} ${o.child_last_name}` : ""} is waiting on payment.
                </span>
                <Button size="small" onClick={() => setOtherLabOpen(true)} sx={{ textTransform: "none", flexShrink: 0 }}>Complete payment</Button>
              </Box>
            ))}
          </Alert>
        )}

        <Box sx={{
          borderRadius: 3, p: 3, mb: 4, display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 2, flexWrap: "wrap",
          background: `linear-gradient(135deg, ${BRAND.navy} 0%, #1e2a5e 100%)`,
        }}>
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#fff", mb: 0.5 }}>
              Subscriptions &amp; Invoices
            </Typography>
            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
              See every invoice, update your card, or manage your $0.99/mo subscription — securely, on Stripe.
            </Typography>
          </Box>
          <Button
            variant="contained" onClick={handleOpenBillingPortal} disabled={portalLoading}
            startIcon={portalLoading ? <CircularProgress size={16} sx={{ color: BRAND.navy }} /> : null}
            sx={{
              bgcolor: "#fff", color: BRAND.navy, fontWeight: 700, textTransform: "none",
              borderRadius: "10px", px: 3, py: 1.2, whiteSpace: "nowrap",
              "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
            }}
          >
            {portalLoading ? "Opening…" : "Manage Billing"}
          </Button>
        </Box>

        <Box sx={{ borderRadius: 3, overflow: "hidden", border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`, bgcolor: cardBg, mb: 4 }}>
          <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)"}` }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.08em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
              Proove Lab Test Co-pay
            </Typography>
          </Box>
          <Box sx={{ px: 3, py: 2.5 }}>
            {copayItem && (
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                    {formatCents(copayItem.amount_cents)} — {copayItem.label}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: isDark ? "#64748b" : "#94a3b8", mt: 0.25 }}>
                    {copayRequest?.payment_preference === "later"
                      ? "You chose to pay this later at registration."
                      : "You chose to pay this now at registration."}
                  </Typography>
                  {copayRequest?.receipt_url && (
                    <Box component="a" href={copayRequest.receipt_url} target="_blank" rel="noopener noreferrer"
                      sx={{ fontSize: 12, color: BRAND.accent, fontWeight: 600, mt: 0.5, display: "inline-block" }}>
                      View receipt →
                    </Box>
                  )}
                </Box>
                <Chip
                  label={STATUS_LABEL[copayRequest?.status] || "Pending"}
                  color={STATUS_COLOR[copayRequest?.status] || "warning"}
                />
              </Stack>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 4 }}>
          <Box
            onClick={() => setTelehealthOpen(true)}
            sx={{
              flex: "1 1 260px", cursor: "pointer", borderRadius: 3, p: 2.5,
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`,
              bgcolor: cardBg, transition: "border-color 0.15s",
              "&:hover": { borderColor: BRAND.accent },
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b", mb: 0.5 }}>
              Order Telehealth (KidSafe)
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: isDark ? "#64748b" : "#94a3b8" }}>
              For kids under 18 — intake, payment, and clinician review, same as at registration.
            </Typography>
          </Box>
          <Box
            onClick={() => setOtherLabOpen(true)}
            sx={{
              flex: "1 1 260px", cursor: "pointer", borderRadius: 3, p: 2.5,
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`,
              bgcolor: cardBg, transition: "border-color 0.15s",
              "&:hover": { borderColor: BRAND.accent },
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b", mb: 0.5 }}>
              Upload Another Lab's PGx Results
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: isDark ? "#64748b" : "#94a3b8" }}>
              Have results from a different lab? Get Proove's AI-powered interpretation — charged per upload.
            </Typography>
          </Box>
          <Box
            onClick={() => setPifDialogOpen(true)}
            sx={{
              flex: "1 1 260px", cursor: "pointer", borderRadius: 3, p: 2.5,
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`,
              bgcolor: cardBg, transition: "border-color 0.15s",
              "&:hover": { borderColor: "#e11d48" },
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b", mb: 0.5 }}>
              Pay It Forward — Sponsor a KidSafe Test
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: isDark ? "#64748b" : "#94a3b8" }}>
              $299 covers one KidSafe kit for a family who can't afford one. Choose how many to sponsor.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ borderRadius: 3, overflow: "hidden", border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`, bgcolor: cardBg }}>
          <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)"}` }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.08em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
              Proove Testing Panels
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: isDark ? "#64748b" : "#94a3b8", mt: 0.5 }}>
              Select any additional testing you'd like to purchase — you'll check out for everything selected in one payment.
            </Typography>
          </Box>
          <Box sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {catalog && panelKeys.map((key) => (
              <ItemRow
                key={key}
                itemKey={key}
                item={catalog[key]}
                existing={existingByKey[key]}
                checked={selected.includes(key)}
                onToggle={toggle}
                isDark={isDark}
              />
            ))}
          </Box>
        </Box>

        {/* Always shown (2026-08-17 fix) — was previously only offered when
            a panel was also selected, and duplicated a now-removed section
            above that tried to add "monthly_ai_subscription" into the same
            `selected` array the panels use, which the backend's item_keys
            validation always rejected (that item_key was never in its
            Literal type). This is now the ONLY way to add/buy the
            subscription on this page, works standalone or bundled. */}
        {catalog?.monthly_ai_subscription && existingByKey.monthly_ai_subscription?.status !== "completed" && (
          <Box sx={{ mt: 2, px: 2.5, py: 2, borderRadius: 2, border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`, bgcolor: cardBg }}>
            <FormControlLabel
              control={<Checkbox checked={subscriptionOptin} onChange={(e) => setSubscriptionOptin(e.target.checked)} />}
              label={
                <Typography sx={{ fontSize: 13.5, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                  {selected.length > 0
                    ? `Also add ${catalog.monthly_ai_subscription.label} (${formatCents(catalog.monthly_ai_subscription.amount_cents)}) to this checkout`
                    : `Add ${catalog.monthly_ai_subscription.label} (${formatCents(catalog.monthly_ai_subscription.amount_cents)})`}
                </Typography>
              }
            />
          </Box>
        )}

        <Box sx={{ pt: 3, pb: 1 }}>
          <Button
            fullWidth variant="contained" disabled={(selected.length === 0 && !subscriptionOptin) || submitting}
            onClick={handleSubmit}
            sx={{
              background: `linear-gradient(135deg, ${BRAND.navy} 0%, #004299 100%)`,
              borderRadius: "10px", py: 1.4, fontWeight: 700, textTransform: "none", fontSize: 15,
              boxShadow: "0 8px 24px rgba(10,14,39,0.25)",
            }}
          >
            {submitting
              ? <CircularProgress size={22} sx={{ color: "#fff" }} />
              : selected.length > 0 || subscriptionOptin
                ? `Continue to Checkout (${selected.length + (subscriptionOptin ? 1 : 0)} item${selected.length + (subscriptionOptin ? 1 : 0) > 1 ? "s" : ""})`
                : "Select items to continue"}
          </Button>
        </Box>

        <Typography sx={{ mt: 3, fontSize: 12.5, color: isDark ? "#475569" : "#94a3b8", textAlign: "center" }}>
          Questions about billing?{" "}
          <Box component="a" href="mailto:customerservice@proove.com" sx={{ color: BRAND.accent, fontWeight: 600 }}>
            Contact us
          </Box>
        </Typography>
      </Box>

      <Dialog open={telehealthOpen} onClose={() => !telehealthSubmitting && setTelehealthOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden", bgcolor: isDark ? "rgba(17,24,39,0.97)" : "#ffffff" } }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.accent} 140%)`,
          px: 3.5, py: 3, position: "relative", overflow: "hidden",
        }}>
          <Box sx={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.06)" }} />
          <Box sx={{ width: 44, height: 44, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.14)", mb: 1.5 }}>
            <MedicalServicesIcon sx={{ fontSize: 22, color: "#fff" }} />
          </Box>
          <Typography sx={{ fontSize: 19, fontWeight: 800, color: "#fff", position: "relative" }}>Order Telehealth</Typography>
          <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.8)", mt: 0.25, position: "relative" }}>KidSafe kit — intake, payment, and clinician review</Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {telehealthError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{telehealthError}</Alert>}
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", mb: 1 }}>
            Your info
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }}>
            <TextField label="Your phone" fullWidth size="small" sx={telehealthInputSx} value={parentPhone} onChange={(e) => setParentPhone(sanitizePhoneInput(e.target.value))} />
            <TextField label="Your date of birth" type="date" fullWidth size="small" sx={telehealthInputSx} InputLabelProps={{ shrink: true }}
              value={parentDob} onChange={(e) => setParentDob(e.target.value)} />
          </Stack>
          <KidPicker
            kids={myKids} isDark={isDark} inputSx={telehealthInputSx}
            onPick={(k) => setChild((prev) => ({ ...prev, first_name: k.first_name, last_name: k.last_name, date_of_birth: k.date_of_birth }))}
          />
          <TelehealthDetailsFields
            isDark={isDark} inputSx={telehealthInputSx} accentColor={BRAND.accent}
            child={child} setChild={setChild}
            shippingAddress={shippingAddress} setShippingAddress={setShippingAddress}
            billingAddress={billingAddress} setBillingAddress={setBillingAddress}
            sameAsShipping={sameAsShipping} setSameAsShipping={setSameAsShipping}
            phenotypeAnswers={phenotypeAnswers} setPhenotypeAnswers={setPhenotypeAnswers}
            phqAnswers={phqAnswers} setPhqAnswers={setPhqAnswers}
            medications={medications} setMedications={setMedications}
          />
          <PayItForwardExtra quantity={payItForwardQty} setQuantity={setPayItForwardQty} isDark={isDark} accentColor={BRAND.accent} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setTelehealthOpen(false)} disabled={telehealthSubmitting} sx={{ textTransform: "none", color: isDark ? "#94a3b8" : "#64748b" }}>Cancel</Button>
          <Button
            variant="contained" disabled={!canSubmitTelehealth() || telehealthSubmitting}
            onClick={handleTelehealthSubmit}
            sx={{
              textTransform: "none", borderRadius: "10px", px: 3,
              background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.accent} 140%)`,
              "&.Mui-disabled": { background: isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.12)" },
            }}
          >
            {telehealthSubmitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Continue to Payment"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={otherLabOpen} onClose={() => !otherLabSubmitting && setOtherLabOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden", bgcolor: isDark ? "rgba(17,24,39,0.97)" : "#ffffff" } }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.accent} 140%)`,
          px: 3.5, py: 3, position: "relative", overflow: "hidden",
        }}>
          <Box sx={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.06)" }} />
          <Box sx={{ width: 44, height: 44, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.14)", mb: 1.5 }}>
            <ScienceIcon sx={{ fontSize: 22, color: "#fff" }} />
          </Box>
          <Typography sx={{ fontSize: 19, fontWeight: 800, color: "#fff", position: "relative" }}>Upload Another Lab's Results</Typography>
          <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.8)", mt: 0.25, position: "relative" }}>AI-powered PGx interpretation, $99 per upload</Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {otherLabError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{otherLabError}</Alert>}
          <Alert severity="info" sx={{ borderRadius: 2, mb: 2.5 }}>
            $99, charged per upload (not a one-time fee). Upload the PDF, then pay securely — we'll let you know once it's ready.
          </Alert>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", mb: 1 }}>
            Your info
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }}>
            <TextField label="Your phone" fullWidth size="small" sx={telehealthInputSx} value={parentPhone} onChange={(e) => setParentPhone(sanitizePhoneInput(e.target.value))} />
            <TextField label="Your date of birth" type="date" fullWidth size="small" sx={telehealthInputSx} InputLabelProps={{ shrink: true }}
              value={parentDob} onChange={(e) => setParentDob(e.target.value)} />
          </Stack>

          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Checkbox
                checked={otherLabIsForChild}
                onChange={(e) => setOtherLabIsForChild(e.target.checked)}
              />
            }
            label="This upload is for my child, not myself"
          />
          {otherLabIsForChild && (
            <>
              <KidPicker
                kids={myKids} isDark={isDark} inputSx={telehealthInputSx}
                onPick={(k) => setOtherLabChild({ first_name: k.first_name, last_name: k.last_name, date_of_birth: k.date_of_birth })}
              />
            <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }}>
              <TextField label="Child's first name" fullWidth size="small" sx={telehealthInputSx}
                value={otherLabChild.first_name}
                onChange={(e) => setOtherLabChild((prev) => ({ ...prev, first_name: e.target.value }))} />
              <TextField label="Child's last name" fullWidth size="small" sx={telehealthInputSx}
                value={otherLabChild.last_name}
                onChange={(e) => setOtherLabChild((prev) => ({ ...prev, last_name: e.target.value }))} />
              <TextField label="Child's date of birth" type="date" fullWidth size="small" sx={telehealthInputSx} InputLabelProps={{ shrink: true }}
                value={otherLabChild.date_of_birth}
                onChange={(e) => setOtherLabChild((prev) => ({ ...prev, date_of_birth: e.target.value }))} />
            </Stack>
            </>
          )}

          <Button component="label" variant="outlined" fullWidth
            sx={{
              py: 1.75, textTransform: "none", borderRadius: "10px", fontWeight: 600,
              borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(10,14,39,0.15)",
              color: isDark ? "#e2e8f0" : "#1e293b",
              "&:hover": { borderColor: BRAND.accent, bgcolor: isDark ? "rgba(99,124,219,0.08)" : "rgba(99,124,219,0.05)" },
            }}>
            {otherLabFile ? otherLabFile.name : "Choose file…"}
            <input type="file" accept="application/pdf" hidden onChange={(e) => setOtherLabFile(e.target.files?.[0] || null)} />
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setOtherLabOpen(false)} disabled={otherLabSubmitting} sx={{ textTransform: "none", color: isDark ? "#94a3b8" : "#64748b" }}>Cancel</Button>
          <Button
            variant="contained" disabled={!otherLabFile || !isValidPhone(parentPhone) || !parentDob || !otherLabChildValid || otherLabSubmitting}
            onClick={handleOtherLabSubmit}
            sx={{
              textTransform: "none", borderRadius: "10px", px: 3,
              background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.accent} 140%)`,
              "&.Mui-disabled": { background: isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.12)" },
            }}
          >
            {otherLabSubmitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Continue to Payment"}
          </Button>
        </DialogActions>
      </Dialog>

      <PayItForwardDialog open={pifDialogOpen} onClose={() => setPifDialogOpen(false)} isDark={isDark} accentColor={BRAND.accent} navyColor={BRAND.navy} />
    </Box>
  );
}
